<template>
    <div>

        <vue-headful title="Incident Settings :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light">
                    <router-link :to="`/incidents`">
                        <i class="fa fa-stethoscope"></i> Incidents
                    </router-link>
                </li>
                <li class="breadcrumb-item active">Settings</li>
            </ol>
        </h4>

        <div v-if="loading">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>

            <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
                <template v-for="(tab, key) in settingsTabs">
                    <b-nav-item :active="curTab === key" @click="curTab = key">
                        {{ tab }}
                    </b-nav-item>
                </template>
            </ul>

            <div class="mb-2 mb-lg-0 d-lg-none d-block">
              <b-dropdown :html="tabButtonTitle" variant="default" class="bg-white">
                <template v-for="(tab, key) in settingsTabs">
                    <b-dropdown-item :active="curTab === key" @click="curTab = key">
                        {{ tab }}
                    </b-dropdown-item>
                </template>
              </b-dropdown>
            </div>

            <div class="row">
                <div class="col-12">
                    <div v-if="curTab === 'general'">
                        <div class="card">
                            <div class="card-header">
                                General Incident Settings
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-xl-6 col-lg-6">

                                        <div class="form-group row">
                                            <label class="col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-sm-right">
                                                Require Description on Create:
                                                <i class="fa fa-question-circle" title="This will display and require the incident description when creating the incident."></i>
                                            </label>
                                            <div class="col-lg-8 col-md-7 col-sm-6 col-12 pt-sm-2">
                                                <b-form-checkbox v-model="account.inci_req_desc_on_create" :value="1" :unchecked-value="0" plain />
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label class="col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-sm-right">
                                                Enable "Other Location":
                                                <i class="fa fa-question-circle" title="If enabled, this will turn allow users to input an 'Other Location' instead of a Jobsite record."></i>
                                            </label>
                                            <div class="col-lg-8 col-md-7 col-sm-6 col-12 pt-sm-2">
                                                <b-form-checkbox v-model="account.inci_enable_other_location" :value="1" :unchecked-value="0" plain />
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label class="col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-sm-right">
                                                Enable Edit Lock:
                                                <i class="fa fa-question-circle" title="If enabled, an incident will be locked when checked out for editing. The name and email of the editing user will be displayed to subsequent users who try to edit the incident. Only account admins can override an incident edit lock."></i>
                                            </label>
                                            <div class="col-lg-8 col-md-7 col-sm-6 col-12 pt-sm-2">
                                                <b-form-checkbox v-model="account.inci_enable_edit_lock" :value="1" :unchecked-value="0" plain />
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            <div class="card-footer">
                                <button type="button" class="btn btn-sm btn-success" v-on:click="saveIncidentSettings()" :disabled="saving">
                                    <template v-if="saving">
                                        Updating...
                                    </template>
                                    <template v-else>
                                        Update Incident Settings
                                    </template>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="curTab === 'incident_types'">
                        <div class="card">
                            <div class="card-body">
                                <incident-type-list @inciTypeListCount="((count) => { this.inci_type_count = count; })" />
                            </div>
                        </div>
                    </div>

                    <div v-if="curTab === 'injury_types'">
                        <div class="card">
                            <div class="card-header">
                                Nature of Injury <router-link :to="`/incidents/settings/injury-types/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </div>
                            <div class="card-body">
                                <injury-type-list />
                            </div>
                        </div>
                    </div>

                    <div v-if="curTab === 'body_parts'">
                        <div class="card">
                            <div class="card-header">
                                Major Body Parts
                                <router-link :to="`/incidents/settings/major-body-parts/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </div>
                            <div class="card-body">
                                <major-body-parts-list />
                            </div>
                        </div>
                        <br>
                        <div class="card">
                            <div class="card-header">
                                Specific Body Parts
                                <router-link :to="`/incidents/settings/specific-body-parts/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </div>
                            <div class="card-body">
                                <specific-body-parts-list />
                            </div>
                        </div>
                    </div>

                    <div v-if="curTab === 'root_causes'">
                        <root-cause-settings />
                    </div>

                    <div v-if="curTab === 'facilities'">
                        <div class="card">
                            <div class="card-header">
                                Place of Treatment <router-link :to="`/incidents/settings/facility-types/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </div>
                            <div class="card-body">
                                <facility-type-list />
                            </div>
                        </div>
                    </div>

                    <div v-if="curTab === 'alerts'">
                        <div class="card">
                            <div class="card-header">
                                Email Alert Rules <router-link :to="`/incidents/settings/alerts/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </div>
                            <div class="card-body">
                                <v-client-table ref="emailRulesTable" :columns="columns" :options="options" :data="rules">

                                    <template slot="id" slot-scope="props">
                                        <router-link  :to="`/incidents/settings/alerts/edit/${props.row.id}`" title="Edit Email Alert" class="btn btn-sm btn-light">
                                            <i class="fa fa-edit"></i> Edit
                                        </router-link>
                                        <router-link :to="`/incidents/settings/alerts/delete/${props.row.id}`" title="Delete Email Alert" class="btn btn-sm btn-light">
                                            <span class="text-danger"><i class="fa fa-trash"></i> Delete</span>
                                        </router-link>
                                    </template>

                                    <router-link slot="alert_name" slot-scope="props" :to="`/incidents/settings/alerts/view/${props.row.id}`" title="View Email Alert">
                                        {{ props.row.alert_name }}
                                    </router-link>

                                    <template slot="is_active" slot-scope="props">
                                        <label v-if="props.row.is_active == 1" class="badge badge-success">Active</label>
                                        <label v-if="props.row.is_active == 0" class="badge badge-warning">Inactive</label>
                                    </template>

                                    <template slot="child_row" slot-scope="props">
                                        <div class="row" style="margin-right: -0.625rem; margin-left: -0.625rem;">
                                            <div class="col-md-4 col-12 mb-md-0 mb-3">
                                                <span class="font-weight-bold">Description</span><br>
                                                <div v-html="props.row.description"></div>
                                            </div>
                                            <div class="col-md-4 col-12 mb-md-0 mb-3">
                                                <span class="font-weight-bold">Conditions</span><br>
                                                WHEN:
                                                <ol>
                                                    <li v-for="(cond, key) in props.row.conditions">
                                                        <template v-if="key > 0">AND:</template>
                                                        {{ cond.field_label }} {{ cond.logic }} {{ cond.cond_label }}
                                                    </li>
                                                </ol>
                                            </div>
                                            <div class="col-md-4 col-12 mb-md-0 mb-3">
                                                <span class="font-weight-bold">Recipients</span><br>
                                                Send Email To:
                                                <ol>
                                                    <li v-for="(recip, key) in props.row.recipients">
                                                        {{ recip.target_type }} -
                                                        <template v-if="recip.target_type == 'Employee' || recip.target_type == 'User'">
                                                            {{ recip.target_label.FirstName }} {{ recip.target_label.LastName }}
                                                        </template>
                                                        <template v-else>{{ recip.target_label }}</template>
                                                    </li>
                                                </ol>
                                            </div>
                                        </div>
                                    </template>
                                </v-client-table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                curTab: 'general',
                errors: {},
                account: null,
                inci_type_count: 0,
                rules: [],
                columns: ['id', 'alert_name', 'is_active'],
                options: {
                    columnsDropdown: false,
                    headings: {
                        id: 'Actions',
                        alert_name: 'Name',
                        is_active: 'Status',
                    },
                    orderBy: {
                        ascending: false,
                        column: 'inc_alerts.id',
                    },
                },
            }
        },

        watch: {
            curTab(newValue, oldValue) {
                if (newValue)
                {
                    if (newValue === 'general')
                    {
                        window.history.replaceState(null,null,`${this.$route.path}`)
                    }
                    else
                    {
                        window.history.replaceState(null,null,`${this.$route.path}#${this.curTab}`)
                    }
                }
            },
        },

        computed: {
            tabButtonTitle(){
                //Send it
                return '<i class="fa fa-bars"></i> ' + this.settingsTabs[this.curTab];
            },

            settingsTabs() {
                return {
                    general: 'General',
                    incident_types: 'Incident Types ('+this.inci_type_count+')',
                    injury_types: 'Nature of Injury',
                    body_parts: 'Body Parts',
                    root_causes: 'Root Causes, Hazards, & Contributing Behavior',
                    facilities: 'Place of Treatment',
                    alerts: 'Email Alerts',
                };
            },
        },

        methods: {
            //Load the Account
            getAccount(){
                //Send it
                this.$http.get('admin/incident-settings')
                    .then(res => {
                        //Set data
                        this.account = res.data.account;
                        this.rules = res.data.rules;
                    })
                    .catch(err => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            saveIncidentSettings(){
                //Saving Flag
                this.saving = true;

                //Send it
                this.$http.post('admin/incident-settings', this.account)

                    .then(res => {
                        //Alert
                        this.$toastr.success('Incident Settings Saved');
                    })
                    .catch(err => {

                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            },
        },

        mounted(){
            //Get it
            this.getAccount();

            if (this.$route.hash !== '' && this.$route.hash !== '#')
            {
                this.curTab = this.$route.hash.replace('#','')
            }
        },
    }
</script>
